/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019-2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component, ElementRef, Inject, OnInit, ViewChild,
} from '@angular/core';

import { forOwn } from 'lodash-es';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from 'src/common/services/app-config/app-config.service';
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { FeatureFlagService } from 'src/common/feature-flag.service';
import { ResumeContentSelectionModalComponent }
  from 'src/resume/content-selection-modal/content-selection-modal.component';
import { ErrorService } from 'src/common/error.service';
import { UnitSimpleDTO } from 'src/resume/content-selection-modal/unit-simple-dto.model';
import { ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { ImageCarouselSliderImages, ImageCarouselExtraImages } from '../image-carousel/config';
import { ImageCarouselComponent } from '../image-carousel/image-carousel.component';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { TestCustomDialogContentComponent } from './test-custom-dialog-content/test-custom-dialog-content.component';

/**
 * Angular 8 Testing Component to house all of our Angular8 Component Testing. Helpful for HTML template binding etc...
 */
@Component({
  selector: 'bulb-dev-tools',
  templateUrl: './dev-tools.component.html',
  providers: [
    AeroDialogService,
    ErrorService,
  ],
  standalone: false,
})
export class DevToolsComponent implements OnInit {
  public user: any = {
    firstName: 'Test',
    lastName: 'User',
    birthDate: new Date('05/11/1990'),
    email: 'testuser@bulbapp.com',
    companyDetails: 'Bulb digital portfolios',
    startDate: new Date('01/15/1970'),
    endDate: new Date(),
  };

  /**
   * Needed in order to pass i18n compilation.
   */
  public selectedImage: any;

  /**
   * Reference to any input form controls, mainly for testing.
   */
  public inputFormControl: any = {};

  /**
   * Reference to multiple input form controls, mainly for testing.
   */
  public inputFormMultipleTest: any = {};

  public currentUser: any = {};

  /**
   * All the account feature flags which we show in the feature flags table.
   */
  public featureFlagStatuses = {
    cke: false,
  };

  public sentryEnabledFlag: boolean;

  public buttonlabel;

  public featureFlagIcons = {};

  /**
   * Reference to the selected content by a user from the content-selection modal to render / display.
   */
  public portfolioContentDevTools: Array<UnitSimpleDTO> = new Array<UnitSimpleDTO>();

  /**
   * Swiper image carousel slides.
   */
  public slides: any = ImageCarouselSliderImages;

  /**
   * Swiper extra images to add to the carousel.
   */
  public extraSlides = ImageCarouselExtraImages;

  /**
   * Index of the images to add to the carousel.
   */
  public addCarouselImageIndex = 0;

  /**
   * Image carousel accordion is open.
   */
  public isOpen = true;

  /**
   * Exposing Feature Flag enum so the template can use it.
   */
  public featureFlag = FeatureFlagService.FeatureFlag;

  /**
   * Current index of slide in swiper.
   */
  activeIndex: number;

  /**
   * Hold list of stripe products.
   */
  public stripeProductList: any;

  /**
   * Selected product list.
   */
  public selectedStripeProducts: any = {
    products: [],
  };

  /**
   * Default standard and student product info.
   */
  public defaultStripeProduct: any = {
    standard: {},
    student: {},
  };

  /**
   * Variable containing column size for text area
   */
  public textAreaColumns: number = 80;

  /**
   * message to show whether selected stripe products are saved successfully or not.
   */
  stripeProductAPIResponseMessage: string;

  @ViewChild(ImageCarouselComponent) imageCarouselComponent: ImageCarouselComponent;

  /**
   * message to show content generated successfully.
   */
  pageContentResponse: string;

  /**
   * Message to show whether the page grooming job run successfully or not.
   */
  pageGroomingResponse: string;

  /**
   * Message to show whether job completed successfully or not.
   */
  accountUpdateResponse: string;

  /**
   * Number of Pages to be generated.
   */
  contentPageCount: number;

  /**
   * Message to show whether the email digest sent successfully or not.
   */
  emailDigestResponse: string;

  /**
   * message to show content generated successfully.
   */
  templateListRulesResponse: string;

  /**
   * Message to show whether the Sentry error sent successfully or not.
   */
  sentryResultMessage: string;

  constructor(
    private aeroDialogService: AeroDialogService,
    @Inject('BulbAuthService') private bulbAuthService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    private errorService: ErrorService,
    private featureFlagService: FeatureFlagService,
    private httpClient: HttpClient,
    private appConfig: AppConfigService,
    private elementRef : ElementRef,
  ) {
    this.currentUser = this.bulbAuthService.currentUser;

    forOwn(this.featureFlagStatuses, (value, featureName) => {
      this.featureFlagStatuses[featureName] = this.featureFlagService.isFeatureFlagEnabled(featureName);
      this.featureFlagIcons[featureName] = {
        icon1: '',
        icon2: '',
      };
    });
  }

  /**
   * A lifecycle hook that is called after Angular has initialized.
   * Fetch all products from stripe when the component has initialized.
   */
  ngOnInit(): void {
    if (this.appConfig.stripe?.enabled) {
      this.fetchStripeProducts();
    }
    // This test route requires a user to be logged in.
    if (this.currentUser?.isAuthenticated()) {
      this.toggleSentry(false);
    }
    this.buttonlabel = this.sentryEnabledFlag ? 'Disable' : 'Enable';
  }

  /**
   * Method that tests the creation of the MacMillan 3 Book dialog.
   */
  testBookDialog(): void {
    this.aeroDialogService.showMacMillanBookDialog(false, true);
  }

  /**
   * Method that tests the audio recording dialog.
   */
  testAudioRecord(): void {
    this.aeroDialogService.showAudioRecordingDialog(false);
  }

  /**
   * Method that allows testers to toggle feature flags for the currently logged in account.
   *
   * @param featureName Name of feature flag to toggle.
   */
  public toggleFeatureFlag(featureName) {
    this.httpClient.post(`devtools/toggleAccountFeatureFlag/${featureName}`, {}).toPromise().then(() => {
      // Need to perform a full reload here for the resume feature to take effect.
      window.location.reload();
    });
  }

  /**
   * Method that allows an admin tester to enable a feature flag for all existing accounts.  This will not enable
   * the flag for any accounts created after this is executed.
   *
   * @param featureName to enable.
   */
  public enableFeatureFlagForExistingUsers(featureName) {
    this.featureFlagIcons[featureName].icon1 = 'fas fa-spinner fa-spin';
    this.httpClient.post(`devtools/featureFlagForExistingUsers/${featureName}`, {}).toPromise().then(() => {
      // Need to perform a full reload here for the resume feature to take effect.
      window.location.reload();
    });
  }

  /**
   * Method that allows an admin tester to enable a feature flag globally so that all new accounts have the
   * feature flag enabled.  This will not enable the flag for any accounts already created before this is executed.
   *
   * @param featureName to enable globally.
   */
  public enableGlobalFeatureFlag(featureName) {
    this.featureFlagIcons[featureName].icon2 = 'fas fa-spinner fa-spin';
    this.httpClient.post(`devtools/globalFeatureFlag/${featureName}`, {}).toPromise().then(() => {
      // Don't need to refresh, because this doesn't impact the current account.
      // Doing a small timeout before resetting the icon, to give feed back to the user if the request is
      // very fast.
      setTimeout(() => {
        this.featureFlagIcons[featureName].icon2 = '';
      }, 500);
    });
  }

  /**
   * Method that tests the resume content selection modal.
   */
  showContentSelectionModal(): void {
    const contentSelectionModalOptions: any = {
      width: '100%',
      height: '100%',
      closable: false,
      styleClass: 'resume-content-selection-modal aero-dialog',
    };
    const dialog = this.aeroDialogService.show(
      ResumeContentSelectionModalComponent,
      contentSelectionModalOptions,
    );
    const subscription = dialog.onClose.subscribe((units: any) => {
      subscription.unsubscribe();
      if (units) {
        units.forEach((unit) => {
          this.portfolioContentDevTools.push(unit);
        });
      }
    });
  }

  /**
   * Handle any potential model changes from our aero-input components. This is mainly for testing
   * features and values.
   * @param event
   */
  handleInputModelChanges(event: any): void {
    this.inputFormControl = event;
  }

  /**
   * Same method as above, but for handling multiple angular built in validations.
   * @param event
   */
  handleMultipleInputModelChanges(event: any): void {
    this.inputFormMultipleTest = event;
  }

  /**
   * To set the active index.
   *
   * @param activeIndex desired active index value.
   */
  currentIndexOfSlide(activeIndex) {
    this.activeIndex = activeIndex;
  }

  /**
   * Add the image to the carousel.
   */
  addSlide() {
    this.slides.splice(this.activeIndex, 0, this.extraSlides[this.addCarouselImageIndex]);
    this.slides = this.slides.slice(0);
    this.addCarouselImageIndex = (this.addCarouselImageIndex === this.extraSlides.length - 1)
      ? 0 : this.addCarouselImageIndex + 1;
  }

  /**
   * Remove the image from the carousel.
   */
  removeSlide() {
    this.imageCarouselComponent.removeSlide();
  }

  /**
   * Fetch the list of available products from stripe.
   */
  fetchStripeProducts() {
    this.httpClient.get('devtools/products').toPromise().then((data) => {
      this.stripeProductList = data;

      this.defaultStripeProduct.standard = this.stripeProductList
        .find((product) => product.productName.toLowerCase() === 'bulb+');

      this.defaultStripeProduct.student = this.stripeProductList
        .find((product) => product.productName.toLowerCase() === 'bulb+ student');

      if (this.defaultStripeProduct.standard) {
        this.defaultStripeProduct.standard.productType = 'standard';
        this.selectedStripeProducts.products.push(this.defaultStripeProduct.standard);
      }

      if (this.defaultStripeProduct.student) {
        this.defaultStripeProduct.student.productType = 'student';
        this.selectedStripeProducts.products.push(this.defaultStripeProduct.student);
      }
    });
  }

  /**
   * Add the selected standard product to 'selectedProducts.products' array.
   * @param $event
   */
  setStripeStandardProduct($event) {
    this.addStripeProductDetail($event, 'standard');
  }

  /**
   * Add the selected student product to 'selectedProducts.products' array.
   * @param $event
   */
  setStripeStudentProduct($event) {
    this.addStripeProductDetail($event, 'student');
  }

  /**
   * Method to add selected standard/student product to 'selectedProducts.products' array.
   * @param $event - Detail of selected product.
   * @param type - type of product: [standard, student]
   */
  addStripeProductDetail($event, type) {
    this.stripeProductAPIResponseMessage = '';
    if ($event.selectedOption) {
      const index = this.getIndex(type);
      const productDetail = {
        productId: $event.selectedOption.productId,
        productName: $event.selectedOption.productName,
        productType: type,
      };
      if (index !== -1) {
        this.selectedStripeProducts.products[index] = productDetail;
      } else {
        this.selectedStripeProducts.products.push(productDetail);
      }
    }
  }

  /**
   * Method to get index of product type.
   * @param type - type of product: [standard, student]
   */
  getIndex(type) {
    return this.selectedStripeProducts.products.findIndex((d) => d.productType === type);
  }

  /**
   * Method to send selected products to server for persistence.
   */
  saveProductData() {
    this.stripeProductAPIResponseMessage = '';

    if (this.selectedStripeProducts.products.length === 2) {
      this.bulbBusyCoverService.showBusyDuring(
        this.httpClient.post('devtools/saveproducts', { products: this.selectedStripeProducts.products }).toPromise().then(() => {
          this.stripeProductAPIResponseMessage = 'Product data saved successfully.';
        })
          .catch((error) => {
            this.stripeProductAPIResponseMessage = 'Unable to save products.';
            if (error.data && error.data.developerMessage) {
              this.stripeProductAPIResponseMessage += ` Reason: ${error.data.developerMessage}`;
            }
          }),
      );
    } else if (this.selectedStripeProducts.products.length === 0) {
      this.stripeProductAPIResponseMessage = 'Please select standard/student product.';
    } else {
      const productType = this.getIndex('standard') === -1 ? 'standard' : 'student';
      this.stripeProductAPIResponseMessage = `Please select ${productType} product.`;
    }
  }

  /**
   * Method to create Pages.
   */
  generatePageContent() {
    this.bulbBusyCoverService.showBusyDuring(
      this.httpClient.post('devtools/generateContent', { contentPageCount: this.contentPageCount })
        .toPromise().then(() => {
          this.pageContentResponse = 'Content generated successfully';
        }),
    );
  }

  /**
   * Method to create templates with template list rules.
   */
  generateTemplatesAndListRules() {
    this.bulbBusyCoverService.showBusyDuring(
      this.httpClient.post('devtools/generateTemplatesListRules', {})
        .toPromise().then(() => {
          this.templateListRulesResponse = 'Templates and list rules generated successfully';
        }),
    );
  }

  /**
   * Method to manually trigger the weekly page grooming job.
   */
  runArchivePageGroomingJob() {
    this.pageGroomingResponse = '';
    this.bulbBusyCoverService.showBusyDuring(
      this.httpClient.post('devtools/runWeeklyPageGroomingJob', {})
        .toPromise().then(() => {
          this.pageGroomingResponse = 'Page Grooming Job Completed successfully';
        }, (() => {
          this.pageGroomingResponse = 'Page Grooming Job Failed.';
        })),
    );
  }

  /**
   * Method to manually trigger the weekly Daily/weekly email digest.
   */
  toggleEmailDigest(frequency) {
    this.emailDigestResponse = '';
    this.bulbBusyCoverService.showBusyDuring(
      this.httpClient.post('devtools/toggleEmailDigest', { frequency })
        .toPromise().then(() => {
          this.emailDigestResponse = 'Email digest sent successfully';
        }, (() => {
          this.emailDigestResponse = 'Email digest Failed.';
        })),
    );
  }

  /**
   * Method that allows testers to toggle feature flags for the currently logged in account.
   *
   */
  toggleSentry(isToggle) {
    this.httpClient.get(
      'devtools/toggleSentry',
      {
        params: {
          isToggle,
        },
      },
    ).toPromise().then((res: boolean) => {
      this.sentryEnabledFlag = res;
      this.buttonlabel = this.sentryEnabledFlag ? 'Disable' : 'Enable';
    });
  }

  /**
   * Trigger weekly job for converting Bulb+ accounts to Free.
   */
  convertBulbPlusAccountToFree() {
    this.accountUpdateResponse = '';
    this.bulbBusyCoverService.showBusyDuring(
      this.httpClient.post('devtools/updateAccounts', {})
        .toPromise().then(() => {
          this.accountUpdateResponse = 'Job Completed successfully';
        }, (() => {
          this.accountUpdateResponse = 'Failed.';
        })),
    );
  }

  /**
   * Method that allows testers to manually trigger a Sentry server error from DevTools page.
   * That results in the error being thrown back to the client who then logs it also.
   * Both client and server will see entries on Sentry.
   */
  sendTestSentryError() {
    this.sentryResultMessage = '';
    this.bulbBusyCoverService.showBusyDuring(
      this.httpClient.post('devtools/sendSentryServerError', {}).toPromise().then(() => {
        this.sentryResultMessage = 'Sentry test error sent for server, but not the client';
      }, ((error) => {
        this.errorService.handleException(error);
        this.sentryResultMessage = 'Sentry test error for both server and client sent';
      })),
    );
  }

  /**
   * Example code for displaying a standard aero dialog.
   */
  showStandardDialog() {
    this.aeroDialogService.showDialog({
      title: 'Delete page?',
      // icon: 'far fa-trash-alt',   // left in as easy to change test code
      // iconClass: 'aero-alert',    // left in as easy to change test code
      svgIconName: 'three-arrows',
      detail: 'Are you sure you want to delete this page? You can recover it from recently deleted items.',
      closable: true,
      primaryStyle: ButtonStyle.NEGATIVE_ACTION,
      primaryLabel: 'Delete',
      onPrimary: () => { alert('primary'); },     // eslint-disable-line
      onSecondary: () => { alert('secondary'); }, // eslint-disable-line
      onClose: () => { alert('close'); },         // eslint-disable-line
      closeOnPrimary: true,
      closeOnSecondary: true,
      focusAfterClosed: this.elementRef.nativeElement.querySelector('#testFocusInput input'),
      customContentComponent: TestCustomDialogContentComponent,
    });
  }

  /**
   * Example code for displaying a standard aero input dialog.
   */
  showStandardDialogInput() {
    this.aeroDialogService.showDialog({
      title: 'Submit Form?',
      showInputField: true,
      dialogInput: {
        inputLabel: 'Name',
        inputText: 'Test',
        onInputChange: (value) => {console.log(value);}, // eslint-disable-line
      },
      closable: true,
      primaryStyle: ButtonStyle.PRIMARY,
      primaryLabel: 'Submit',
      onPrimary: () => { alert('primary'); },     // eslint-disable-line
      onSecondary: () => { alert('secondary'); }, // eslint-disable-line
      onClose: () => { alert('close'); },         // eslint-disable-line
      closeOnPrimary: true,
      closeOnSecondary: true,
      focusAfterClosed: this.elementRef.nativeElement.querySelector('#testFocusInput input'),
    });
  }
}
